import dayjs, {type Dayjs} from 'dayjs';
import {makeAutoObservable, reaction, when} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {CalendarDatePickerMarkedDates} from '../../components/CalendarDatePicker/types';
import type {ThemeColor} from '../../styles';
import type {Nullable} from '../../utils/utility-types';
import type {TimeslotModel} from './Timeslot.model';
import type {
  CommonGetters,
  MarkedDateConfig,
  MarkedDates,
  Strategy,
} from './types';

import {currentUserStore} from '../../api/user';
import {alert} from '../../components/alert';
import {conferenceCreate} from '../../services/entity-action/conference/conference.create';
import {getTimezoneOffset, type Timestamp} from '../../utils/datetime';
import {logger} from '../../utils/logger';
import {
  AsyncBusyManager,
  type Destructible,
  ReactionDisposeManager,
  type WithBusyManager,
} from '../../utils/store';
import {CalendarController} from './Calendar.controller';
import {TimeslotsStore} from './Timeslots.store';
import {
  DATE_KEY_FORMAT,
  LONG_DATE_FORMAT,
  SHORT_DATE_FORMAT,
} from './utils/constants';
import {showSuccessBookingAlert} from './utils/showSuccessBookingAlert';

export class BookConferenceStore<S extends Strategy = Strategy>
  implements
    CommonGetters,
    Destructible,
    WithBusyManager<BookConferenceStore.BusyKey>
{
  availableSlotColor: ThemeColor = 'background.secondary';
  readonly busy = new AsyncBusyManager<BookConferenceStore.BusyKey>();
  calendar: CalendarController = new CalendarController();
  currentUserTimezone: string;
  dataTargetMarkedDates: Nullable<MarkedDates> = null;
  disposer = new ReactionDisposeManager();
  strategy: S;
  timeSlots = new TimeslotsStore(this.calendar);
  unavailableSlotColor: ThemeColor = 'background.grayLight';
  private customBookFn: Nullable<BookConferenceStore.CustomBookFn> = null;

  constructor(strategy: S, shouldFetchTimeslots = true) {
    this.strategy = strategy;

    this.initCurrentUserTimezone();

    this.busy.observe(this, {book: 'book'});
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });

    this.strategy
      .inject(this.calendar, this.timeSlots)
      .setupReactions(shouldFetchTimeslots)
      .init();

    this.setupReactions();
  }

  destruct() {
    this.strategy.destruct();

    this.disposer.dispose();
  }

  get availableConferences() {
    return this.strategy.dataTarget?.availableConferences ?? 0;
  }

  get bookingDateToString() {
    if (!this.timeSlots.selected) {
      return '';
    } else {
      const start = this.timeSlots.selected.startDateDjs;

      return BookConferenceStore.bookDateToString(
        start,
        start.add(this.conferenceDuration || 0, 'minute'),
      );
    }
  }

  get bookingRange() {
    return (
      this.strategy.dataTarget?.bookingRange ?? {
        end: Date.now(),
      }
    );
  }

  get conferenceCount() {
    return this.strategy.dataTarget?.conferenceCount ?? 0;
  }

  get conferenceDuration() {
    return this.strategy.dataTarget?.conferenceDuration ?? 0;
  }

  get currentUserTimezoneString() {
    return getTimezoneOffset(this.currentUserTimezone);
  }

  get firstAvailableDate(): Timestamp {
    const available = this.dataTargetMarkedDates?.available;

    if (!available) {
      return this.calendar.selectedDate;
    }

    const firstAvailableDate = Array.from(available.keys())[0];
    const timestamp = dayjs(firstAvailableDate).valueOf();

    return Math.max(timestamp, this.calendar.selectedDate);
  }

  get isBookButtonDisabled() {
    return Boolean(this.strategy.dataTarget?.isBookButtonDisabled);
  }

  get isSessionLimitReachedForToday() {
    if (!this.strategy.dataTarget) {
      return false;
    }

    const currentDay = dayjs(this.calendar.selectedDate).format(
      DATE_KEY_FORMAT,
    );

    if (!this.timeSlots.todaySlots.length) {
      return false;
    }

    const {unavailable} = this.strategy.dataTarget.getMarkedDates(
      new Map([[currentDay, this.timeSlots.todaySlots]]),
      this.strategy.conferenceToRescheduleId ?? undefined,
    );

    return unavailable.has(currentDay);
  }

  get isShowBuyMoreButton() {
    return this.strategy.isShowBuyMoreButton;
  }

  get markedDates(): CalendarDatePickerMarkedDates {
    return Array.from(this.nativeMarkedDates.entries()).reduce(
      (acc, [date, config]) => ({
        ...acc,
        [date]: {
          color: config.isAvailableForBooking ? 'text.secondary' : undefined,
          fill: {
            alpha: config.isAvailableForBooking ? 0.1 : undefined,
            color: config.isAvailableForBooking
              ? this.availableSlotColor
              : this.unavailableSlotColor,
            variant: config.isCoachCoverage ? 'stripped' : undefined,
          },
        },
      }),
      {} as CalendarDatePickerMarkedDates,
    );
  }

  get nativeMarkedDates() {
    const result = new Map<string, MarkedDateConfig>();

    if (!this.dataTargetMarkedDates) {
      return result;
    }

    const newCoachDates = new Set(this.timeSlots.renderNewCoachSlots.keys());

    [
      ...Array.from(this.dataTargetMarkedDates.available.keys()),
      ...Array.from(this.dataTargetMarkedDates.unavailable.keys()),
    ].forEach(date => {
      const isDateAvailable = this.dataTargetMarkedDates!.available.has(date);

      result.set(date, {
        isAvailableForBooking: isDateAvailable,
        isCoachCoverage: newCoachDates.has(date),
      });
    });

    return result;
  }

  get shouldShowAvailableConferences() {
    return this.strategy.shouldShowAvailableConferences;
  }

  get shouldShowBookButton() {
    return this.strategy.shouldShowBookButton;
  }

  get title() {
    return this.strategy.title;
  }

  get todayAvailableTimeRange() {
    const selectedDate = dayjs(this.calendar.selectedDate).format(
      DATE_KEY_FORMAT,
    );

    return this.dataTargetMarkedDates?.available.get(selectedDate) ?? {};
  }

  get todayTimeslots() {
    const slots = this.timeSlots.todaySlots;
    const {from, to} = this.todayAvailableTimeRange;

    if (from || to) {
      slots.forEach(chunk =>
        chunk.forEach(slot => {
          if (from && slot.startDateTimestamp < from) {
            slot.isActive.setFalse();
          }

          if (to && slot.startDateTimestamp > to) {
            slot.isActive.setFalse();
          }
        }),
      );
    }

    return slots;
  }

  static bookDateToString(startDjs: Dayjs, endDjs: Dayjs) {
    const start = startDjs.format(LONG_DATE_FORMAT);

    const end = endDjs.format(SHORT_DATE_FORMAT);

    return `${start} - ${end}`;
  }

  async book() {
    try {
      if (!this.timeSlots.selected) {
        throw new Error('No selected slot');
      }

      await this.strategy.refreshSlots({isSilent: true});

      const selectedSlot = this.timeSlots.selected;

      if (selectedSlot.isActive.isFalse) {
        this.timeSlots.clearSelectedSlot();

        throw new Error(i18n.t('shared:time_slot_not_available'));
      }

      if (this.customBookFn) {
        await this.customBookFn(selectedSlot);

        return;
      }

      if (!this.strategy.isCoverageCoachAvailableForSlot(selectedSlot)) {
        throw new Error(
          i18n.t('shared:book_conference.coverage_not_started_error', {
            date: dayjs(this.strategy.coachCoverage!.period.start).format('LL'),
          }),
        );
      }

      const params = this.strategy.makeRequestParams(selectedSlot);

      await this.strategy.deleteConferenceToReschedule();

      await conferenceCreate.asClient({params});

      await this.strategy.refresh();

      await this.strategy.refreshSlots();

      const coachName = this.getCoachNameById(params.coach_ids[0]!);

      await showSuccessBookingAlert({
        coachName,
        dateString: this.bookingDateToString,
        type: this.strategy.conferenceType,
      });

      this.timeSlots.clearSelectedSlot();
    } catch (error) {
      logger.error(error);

      logger.event('session_schedule_error', {
        message: error.message,
      });

      alert.error(error);

      throw error;
    }
  }

  setCustomBookFn(fn: BookConferenceStore.CustomBookFn) {
    this.customBookFn = fn;
  }

  private getCoachNameById(id: string) {
    if (this.strategy.coachCoverage) {
      const {newCoach, oldCoach} = this.strategy.coachCoverage;

      return oldCoach.id === id ? oldCoach.name : newCoach.name;
    }

    return this.strategy.coaches.selected[0]!.name;
  }

  private initCurrentUserTimezone() {
    this.currentUserTimezone = currentUserStore.user?.timezone || 'UTC';

    if (
      !this.currentUserTimezone ||
      (this.currentUserTimezone === 'UTC' && this.strategy.shouldGuessTimezone)
    ) {
      this.currentUserTimezone = dayjs.tz.guess();
    }
  }

  private setupReactions() {
    this.disposer.watch(
      'timeslots_change',
      reaction(
        () => this.timeSlots.renderSlots,
        () => {
          if (!this.strategy.dataTarget) {
            return;
          }

          this.dataTargetMarkedDates = this.strategy.dataTarget.getMarkedDates(
            this.timeSlots.renderSlots,
            this.strategy.conferenceToRescheduleId ?? undefined,
          );
        },
      ),
    );

    this.disposer.watch(
      'set_first_available_date',
      when(
        () => this.timeSlots.isMakingSlotsDone,
        () => {
          this.calendar.setSelectedDate(this.firstAvailableDate);
        },
      ),
    );
  }
}

export namespace BookConferenceStore {
  export type BusyKey = 'book';

  export type CustomBookFn = (timeslot: TimeslotModel) => Promise<void>;
}
